import { AxiosError } from "axios";
import { useQuery } from "react-query";
import _keyBy from "lodash/keyBy";
import _map from "lodash/map";
import { useErrorBoundary } from "component-library";
import {
  FacetCategory,
  GET_FACETS_CATEGORY_QUERY_KEY,
  GET_LOCATIONS_BY_ID,
  getFacetsCategories,
  IGetFacetsResponse,
  getGoogleLocationsByIds,
} from "shared-services";
import { useTranslation } from "i18n";
import { authAxios as axios } from "shared-auth";
import { HALManager, UserManager } from "core";
import { BasicInfo } from "models";
import BasicInfoLocation from "models/BasicInfo/BasicInfoLocation";
import IProfileViewLocation from "models/ProfileView/ProfileViewLocation";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import { getApiUrl } from "services/UrlBuilder";
import { IBasicInfoViewModel } from "pages/MainPage/components/BasicInfoSection/viewModels";
import { convertBasicInfoToView } from "pages/Profile/helpers/basic-info-converter/convert-basic-info-to-view";

const GET_BASIC_INFO_QUERY_KEY = "GET_BASIC_INFO_QUERY_KEY";

type LocationsInterface = BasicInfoLocation | IProfileViewLocation;

const getLocationId = (loc: LocationsInterface): string =>
  "googlePlaceId" in loc ? loc.googlePlaceId : loc.locationId;

export function useGetBasicInfo() {
  const { t } = useTranslation();
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();
  const { data: publicProfileData, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const { data: facets = [], isLoading: isFacetsLoading } = useQuery<
    IGetFacetsResponse,
    AxiosError
  >(
    GET_FACETS_CATEGORY_QUERY_KEY,
    async () => {
      const { data } = await getFacetsCategories(Object.values(FacetCategory));

      return data;
    },
    {
      onError: captureError,
    }
  );

  const { data: privateBasicInfoData, isLoading: isPrivateBasicInfoLoading } =
    useQuery<BasicInfo, AxiosError>(
      GET_BASIC_INFO_QUERY_KEY,
      async () => {
        const { data } = await axios.get(
          getApiUrl(HALManager.getBasicInfoUrl(UserManager.userId))
        );

        return data;
      },
      {
        enabled: !isPublicView,
        cacheTime: 0,
        onError: captureError,
      }
    );

  const basicInfoData = isPublicView
    ? publicProfileData?.basicInfo
    : privateBasicInfoData;

  const profileLocation = (basicInfoData?.locations ||
    []) as LocationsInterface[];

  const profileLocationIds: string[] = profileLocation.map(getLocationId);

  const { data: googleLocationsData, isLoading: googleLocationsLoading } =
    useQuery(
      [GET_LOCATIONS_BY_ID, profileLocationIds],
      () => getGoogleLocationsByIds(profileLocationIds),
      {
        enabled: !!profileLocationIds.length,
      }
    );

  const dataById = _keyBy(googleLocationsData, "googlePlaceId");

  const locationsDetail = _map(profileLocation, (location) => {
    return {
      ...location,
      ...dataById[getLocationId(location)],
    };
  });

  const isLoading =
    isPublicProfileLoading ||
    isFacetsLoading ||
    isPrivateBasicInfoLoading ||
    googleLocationsLoading;

  const basicInfo =
    basicInfoData && !isLoading
      ? convertBasicInfoToView({
          basicInfo: basicInfoData,
          locations: locationsDetail,
          facets,
          isViewMode: isPublicView,
          t,
        })
      : ({} as IBasicInfoViewModel);

  return {
    basicInfo,
    isLoading,
    basicInfoConfiguration: {
      isVisibleActualAge: basicInfoData?.isVisibleActualAge,
      isVisibleDisability: basicInfoData?.isVisibleDisability,
      isVisibleGender: basicInfoData?.isVisibleGender,
      isVisibleHeritageAndEthnicity:
        basicInfoData?.isVisibleHeritageAndEthnicity,
      isVisibleVisasAndPermits: basicInfoData?.isVisibleVisasAndPermits,
    },
  };
}
